/**
 * GoldSweep.js — Radial Cloud Burst (Mixed palette)
 * Soft volumetric cloud puffs expanding radially from screen centre.
 * Same public interface (models, smokeAnimation, tick) — nothing else changes.
 */
export default class GoldSweep {
    constructor() {
        this.models     = [];
        this._canvas    = null;
        this._ctx       = null;
        this._animating = false;
        this._puffs     = [];
        this._raf       = null;
        this._setupCanvas();
    }

    _setupCanvas() {
        this._canvas = document.createElement('canvas');
        this._canvas.style.cssText = [
            'position:fixed', 'top:0', 'left:0',
            'width:100%', 'height:100%',
            'pointer-events:none',
            'z-index:9998',
        ].join(';');
        document.body.appendChild(this._canvas);
        this._ctx = this._canvas.getContext('2d');
        this._fit();
        window.addEventListener('resize', () => this._fit(), { passive: true });
    }

    _fit() {
        const dpr = Math.min(window.devicePixelRatio, 2);
        this._canvas.width  = window.innerWidth  * dpr;
        this._canvas.height = window.innerHeight * dpr;
    }

    // ── Palette 5: Desert Rose Gold — warm blush + gold, no purple ────
    _pickColour() {
        const picks = [
            { hue: 28, sat: 45, lit: 82 }, // warm peach blush
            { hue: 32, sat: 52, lit: 78 }, // soft apricot
            { hue: 24, sat: 40, lit: 84 }, // pale blush rose
            { hue: 30, sat: 58, lit: 74 }, // deeper warm rose
            { hue: 36, sat: 35, lit: 86 }, // golden cream edge
        ];

        const p = picks[Math.floor(Math.random() * picks.length)];

        return {
            hue: p.hue + (Math.random() - 0.5) * 5,
            sat: p.sat + (Math.random() - 0.5) * 6,
            lit: p.lit + (Math.random() - 0.5) * 5,
        };
    }

    _makePuff() {
        const w     = this._canvas.width;
        const h     = this._canvas.height;
        const scale = h / 800;

        const ox    = w * 0.5 + (Math.random() - 0.5) * w * 0.06;
        const oy    = h * 0.5 + (Math.random() - 0.5) * h * 0.06;
        const angle = Math.random() * Math.PI * 2;
        const spd   = (0.7 + Math.random() * 2.8) * scale;
        const col   = this._pickColour();

        return {
            x: ox, y: oy,
            vx: Math.cos(angle) * spd,
            vy: Math.sin(angle) * spd,
            drag:    0.965,
            gravity: 0.003 * scale,
            wAmp:    (Math.random() - 0.5) * 0.03,
            wFreq:   0.018 + Math.random() * 0.025,
            wPh:     Math.random() * Math.PI * 2,
            r:       (18 + Math.random() * 28) * scale,
            expand:  (0.55 + Math.random() * 0.7) * scale,
            alpha:   0.09 + Math.random() * 0.13,
            fade:    0.0013 + Math.random() * 0.0018,
            hue:     col.hue,
            sat:     col.sat,
            lit:     col.lit,
            t: 0, delay: 0,
        };
    }

    smokeAnimation(onMidpoint) {
        if (this._animating) return;
        this._animating = true;
        this._puffs     = [];
        this._fit();

        const isMobile      = window.innerWidth < 768;
        const waves         = isMobile ? 5 : 8;
        const perWave       = isMobile ? 10 : 16;
        const waveDelay     = 85;
        const totalDuration = waves * waveDelay + 1200;

        if (typeof onMidpoint === 'function') {
            setTimeout(() => onMidpoint(), totalDuration * 0.4);
        }

        for (let w = 0; w < waves; w++) {
            setTimeout(() => {
                for (let i = 0; i < perWave; i++) {
                    const p = this._makePuff();
                    p.delay = Math.floor(Math.random() * 10);
                    this._puffs.push(p);
                }
            }, w * waveDelay);
        }

        const step = () => {
            const cv  = this._canvas;
            const ctx = this._ctx;
            ctx.clearRect(0, 0, cv.width, cv.height);

            let alive = false;

            this._puffs.forEach(p => {
                if (p.delay > 0) { p.delay--; alive = true; return; }

                p.t++;
                p.vx  = p.vx * p.drag + Math.sin(p.t * p.wFreq + p.wPh) * p.wAmp;
                p.vy  = p.vy * p.drag + p.gravity;
                p.x  += p.vx;
                p.y  += p.vy;
                p.r  += p.expand;
                p.alpha = Math.max(0, p.alpha - p.fade);

                if (p.alpha <= 0) return;
                alive = true;

                const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
                g.addColorStop(0,   `hsla(${p.hue},${p.sat}%,${Math.min(98, p.lit + 8)}%,${p.alpha})`);
                g.addColorStop(0.35, `hsla(${p.hue},${p.sat}%,${p.lit}%,${p.alpha * 0.55})`);
                g.addColorStop(1,   `hsla(${p.hue},${Math.max(30, p.sat - 20)}%,${Math.min(86, p.lit + 14)}%,0)`);

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle   = g;
                ctx.globalAlpha = 1;
                ctx.fill();
            });

            this._puffs = this._puffs.filter(p => p.alpha > 0 || p.delay > 0);

            if (alive) {
                this._raf = requestAnimationFrame(step);
            } else {
                ctx.clearRect(0, 0, cv.width, cv.height);
                this._animating = false;
            }
        };

        setTimeout(() => step(), 20);
    }

    tick() {}
}